import { useState, useEffect } from 'react';
import { useAuthStore } from '../store/auth';
import {
  FileText,
  Download,
  RefreshCw,
  Search,
  Filter,
  RotateCcw,
  AlertCircle,
} from 'lucide-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Table from '../components/ui/Table';

const providerLogos: Record<string, string> = {
  paypal: '/images/providers/paypal.png',
  wise: '/images/providers/wise.svg',
  stripe: '/images/providers/stripe.png',
  binance: '/images/providers/binance.png',
};

const providerBgColors: Record<string, string> = {
  paypal: '#ffffff',
  wise: '#f0fbe8',
  stripe: '#f5f5ff',
  binance: '#1E2329',
};

const providerNames: Record<string, string> = {
  paypal: 'PayPal',
  wise: 'Wise',
  stripe: 'Stripe',
  binance: 'Binance Pay',
};

interface Invoice {
  id: string;
  invoiceNumber: string;
  amount: string | number;
  currency: string;
  feeAmount: string | number;
  netAmount: string | number;
  provider: string;
  status: string;
  issuedAt: string;
  paidAt: string | null;
  downloadCount: number;
  payoutRequest: {
    affiliate: {
      id: string;
      name: string;
      email: string;
    };
    paymentMethod: {
      provider: string;
      accountId: string;
    };
  };
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export default function InvoicesPage() {
  const { accessToken } = useAuthStore();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [regenerating, setRegenerating] = useState<string | null>(null);

  const API_BASE = import.meta.env.VITE_API_URL || '';

  useEffect(() => {
    fetchInvoices();
  }, [pagination.page, providerFilter, statusFilter, dateRange]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(pagination.page),
        limit: String(pagination.limit),
      });

      if (search) params.append('search', search);
      if (providerFilter) params.append('provider', providerFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (dateRange.start) params.append('startDate', dateRange.start);
      if (dateRange.end) params.append('endDate', dateRange.end);

      const response = await fetch(`${API_BASE}/api/v1/invoices?${params}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-Tenant-ID': 'demo-casino',
        },
      });

      const data = await response.json();
      if (data.success) {
        setInvoices(data.data);
        setPagination(data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchInvoices();
  };

  const handleDownload = async (invoiceId: string, invoiceNumber: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/invoices/${invoiceId}/download`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-Tenant-ID': 'demo-casino',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download invoice');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download invoice:', error);
      alert('Failed to download invoice');
    }
  };

  const handleRegenerate = async (invoiceId: string) => {
    if (!confirm('Are you sure you want to regenerate this invoice?')) return;

    setRegenerating(invoiceId);
    try {
      const response = await fetch(`${API_BASE}/api/v1/invoices/${invoiceId}/regenerate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-Tenant-ID': 'demo-casino',
        },
      });

      const data = await response.json();
      if (data.success) {
        alert('Invoice regenerated successfully');
        fetchInvoices();
      } else {
        alert(data.error || 'Failed to regenerate invoice');
      }
    } catch (error) {
      console.error('Failed to regenerate invoice:', error);
      alert('Failed to regenerate invoice');
    } finally {
      setRegenerating(null);
    }
  };

  const formatAmount = (amount: string | number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(typeof amount === 'string' ? parseFloat(amount) : amount);
  };

  const formatDate = (date: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'info' | 'warning'> = {
      generated: 'success',
      downloaded: 'info',
      sent: 'info',
    };
    return <Badge variant={variants[status] || 'success'}>{status}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--text-primary)]">Invoices</h1>
          <p className="mt-2 text-sm text-[var(--text-secondary)]">
            View and manage all generated invoices for completed payouts.
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <Button variant="secondary" onClick={fetchInvoices} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
              <input
                type="text"
                placeholder="Invoice number or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="input pl-10 w-full"
              />
            </div>
          </div>

          {/* Provider Filter */}
          <div className="w-40">
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
              Provider
            </label>
            <select
              value={providerFilter}
              onChange={(e) => setProviderFilter(e.target.value)}
              className="input w-full"
            >
              <option value="">All Providers</option>
              <option value="paypal">PayPal</option>
              <option value="wise">Wise</option>
              <option value="stripe">Stripe</option>
              <option value="binance">Binance Pay</option>
            </select>
          </div>

          {/* Status Filter */}
          <div className="w-40">
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input w-full"
            >
              <option value="">All Status</option>
              <option value="generated">Generated</option>
              <option value="downloaded">Downloaded</option>
              <option value="sent">Sent</option>
            </select>
          </div>

          {/* Date Range */}
          <div className="flex gap-2">
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                From
              </label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange((prev) => ({ ...prev, start: e.target.value }))}
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                To
              </label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange((prev) => ({ ...prev, end: e.target.value }))}
                className="input"
              />
            </div>
          </div>

          <Button variant="primary" onClick={handleSearch}>
            <Filter className="w-4 h-4 mr-2" />
            Apply
          </Button>
        </div>
      </Card>

      {/* Invoices Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      ) : (
        <Card>
          <Table
            columns={[
              {
                header: 'Invoice #',
                accessor: (row: Invoice) => (
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-primary-400" />
                    <span className="font-mono font-medium text-[var(--text-primary)]">
                      {row.invoiceNumber}
                    </span>
                  </div>
                ),
              },
              {
                header: 'Affiliate',
                accessor: (row: Invoice) => (
                  <div>
                    <div className="font-medium text-[var(--text-primary)]">
                      {row.payoutRequest.affiliate.name}
                    </div>
                    <div className="text-sm text-[var(--text-muted)]">
                      {row.payoutRequest.affiliate.email}
                    </div>
                  </div>
                ),
              },
              {
                header: 'Amount',
                accessor: (row: Invoice) => (
                  <div>
                    <div className="font-semibold text-[var(--text-primary)]">
                      {formatAmount(row.netAmount)}
                    </div>
                    <div className="text-xs text-[var(--text-muted)]">
                      Gross: {formatAmount(row.amount)} | Fee: {formatAmount(row.feeAmount)}
                    </div>
                  </div>
                ),
              },
              {
                header: 'Provider',
                accessor: (row: Invoice) => (
                  <div className="flex items-center gap-2">
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center p-1"
                      style={{ backgroundColor: providerBgColors[row.provider] || '#ffffff' }}
                    >
                      <img
                        src={providerLogos[row.provider]}
                        alt={row.provider}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>
                    <span className="text-sm text-[var(--text-primary)]">
                      {providerNames[row.provider] || row.provider}
                    </span>
                  </div>
                ),
              },
              {
                header: 'Issued',
                accessor: (row: Invoice) => (
                  <div>
                    <div className="text-sm text-[var(--text-primary)]">{formatDate(row.issuedAt)}</div>
                    {row.paidAt && (
                      <div className="text-xs text-[var(--text-muted)]">Paid: {formatDate(row.paidAt)}</div>
                    )}
                  </div>
                ),
              },
              {
                header: 'Status',
                accessor: (row: Invoice) => (
                  <div className="flex items-center gap-2">
                    {getStatusBadge(row.status)}
                    {row.downloadCount > 0 && (
                      <span className="text-xs text-[var(--text-muted)]">
                        ({row.downloadCount} downloads)
                      </span>
                    )}
                  </div>
                ),
              },
              {
                header: 'Actions',
                accessor: (row: Invoice) => (
                  <div className="flex gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleDownload(row.id, row.invoiceNumber)}
                    >
                      <Download className="w-3 h-3 mr-1" />
                      Download
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleRegenerate(row.id)}
                      disabled={regenerating === row.id}
                    >
                      {regenerating === row.id ? (
                        <RefreshCw className="w-3 h-3 animate-spin" />
                      ) : (
                        <RotateCcw className="w-3 h-3" />
                      )}
                    </Button>
                  </div>
                ),
              },
            ]}
            data={invoices}
          />

          {invoices.length === 0 && (
            <div className="text-center py-12 text-[var(--text-muted)]">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No invoices found</p>
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-[var(--border-color)] px-4 py-3">
              <div className="text-sm text-[var(--text-muted)]">
                Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}{' '}
                invoices
              </div>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={pagination.page === 1}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={pagination.page === pagination.totalPages}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
